import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { Box } from "orcs-design-system";
import themeGet from "@styled-system/theme-get";
import PropTypes from "prop-types";
import { useAISidebar } from "src/hooks/useAISidebar";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import AIChatInterface from "../AIChatInterface";
import { AIChatProvider } from "../context/AIChatProviderWithAgentCore";
import { AGENTCORE_NAMES, SOURCE_TYPES } from "../consts";
import { AIChatV3Provider } from "./AIChatV3Provider";
import AIChatV3Interface from "./AIChatV3Interface";

const V2_AGENTS = new Set([
  AGENTCORE_NAMES.ASK_TEAMFORM_V2,
  AGENTCORE_NAMES.GQL_EXPERT_V2,
]);

const AIChatWindowWrapper = styled(Box)`
  ${({ isOpen }) =>
    !isOpen &&
    css`
      display: none;
    `}

  ${({ aiViewMode }) =>
    aiViewMode === "fullscreen" &&
    css`
      flex: 1;
      padding: ${themeGet("space.r")};
    `}
`;

/**
 * Top-level AI chat window.
 *
 * Owns `selectedAgent` state and routes to the appropriate frontend:
 * - v2 agents (ask_teamform_v2, gql_expert_v2) → v3 frontend
 * - v1 agent  (ask_teamform)                   → v2 frontend
 */
const AIChatV3Window = ({ isOpen }) => {
  const { closeAI } = useAISidebar();
  const { enableAskTeamformV2 } = useWorkspaceFeatureFlags();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const aiViewMode = query.get("aiViewMode");

  const [selectedAgent, setSelectedAgent] = useState(
    enableAskTeamformV2
      ? AGENTCORE_NAMES.ASK_TEAMFORM_V2
      : AGENTCORE_NAMES.ASK_TEAMFORM
  );

  const isV2Agent = V2_AGENTS.has(selectedAgent);
  const variantProp = aiViewMode === "fullscreen" ? "fullpage" : "sidebar";
  const containerWidth =
    aiViewMode === "fullscreen"
      ? undefined
      : ["100%", "280px", "300px", "350px", "360px", "380px"];

  const interfaceProps = {
    variant: variantProp,
    showCloseButton: true,
    onClose: closeAI,
    headerTitle: "Ask TeamForm",
    showChatHistoryToggle: true,
    showNewChatButton: true,
  };

  return (
    <AIChatWindowWrapper
      aiViewMode={aiViewMode}
      isOpen={isOpen}
      width={containerWidth}
    >
      {isV2Agent ? (
        <AIChatV3Provider
          sourceId={SOURCE_TYPES.chat_q_and_a}
          selectedAgent={selectedAgent}
          setSelectedAgent={setSelectedAgent}
        >
          <AIChatV3Interface {...interfaceProps} />
        </AIChatV3Provider>
      ) : (
        <AIChatProvider
          sourceId={SOURCE_TYPES.chat_q_and_a}
          selectedAgent={selectedAgent}
          setSelectedAgent={setSelectedAgent}
        >
          <AIChatInterface {...interfaceProps} />
        </AIChatProvider>
      )}
    </AIChatWindowWrapper>
  );
};

AIChatV3Window.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default AIChatV3Window;
